// Pages/Blog/BlogEditable.jsx
import React, { useEffect, useState, useCallback, useRef, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";

import Content from "../../Components/Core/Content";
import FormInput from "../../Components/Core/FormInput";
import Loader from "../../Components/Core/Loader";
import Notification from "../../Components/Core/Notification";

import { createBlog, editBlog, getBlogById } from "../../Services/BlogService";
import { ViewAction } from "../../Enums/ViewActionEnum";
import { getDecodedUserId } from "../../Services/GeneralService";

import "./blog-editable.css";

const ArticleEditor = lazy(() => import("../../Components/Blog/ArticleEditor"));


const BlogEditable = ({ action }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    abstract: "",
    description: "",
    image: null,
    imagePreview: null,
  });

  // UI State
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Determine page title and mode
  const isEditMode = action === ViewAction.EDIT;
  const pageTitle = isEditMode ? "ویرایش مقاله" : "ایجاد مقاله جدید";

  // ============================================
  // Handlers
  // ============================================

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    const description = editorRef.current?.getHTML() ?? formData.description;
    // Validation
    if (!formData.title.trim()) {
      setSnackbarMessage("لطفاً عنوان مقاله را وارد کنید");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (!formData.abstract.trim()) {
      setSnackbarMessage("لطفاً چکیده مقاله را وارد کنید");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (!new DOMParser().parseFromString(description, "text/html").body.textContent.trim()) {
      setSnackbarMessage("لطفاً محتوای مقاله را وارد کنید");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (!isEditMode && !formData.image) {
      setSnackbarMessage("لطفاً تصویر شاخص مقاله را انتخاب کنید");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const data = {
        Image: formData.image,
        Title: formData.title,
        Description: description,
        Abstract: formData.abstract,
        AuthorId: getDecodedUserId(),
      };

      if (isEditMode) {
        await editBlog(id, data);
        setSnackbarMessage("مقاله با موفقیت ویرایش شد");
      } else {
        await createBlog(data);
        setSnackbarMessage("مقاله با موفقیت ایجاد شد");
      }

      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      setTimeout(() => {
        if (isEditMode) {
          navigate(id ? `/blog/detail/${id}` : "/blog/index");
        } else {
          navigate("/blog/index");
        }
      }, 1000);
    } catch (error) {
      console.error("Error submitting blog:", error);
      setSnackbarMessage(
        error?.message || "خطا در ثبت مقاله. لطفاً دوباره تلاش کنید"
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm("آیا مطمئن هستید؟ تمام تغییرات از دست خواهد رفت.")) {
      setFormData({
        title: "",
        abstract: "",
        description: "",
        image: null,
        imagePreview: null,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // ============================================
  // Effects
  // ============================================

  const fetchData = useCallback(async () => {
    if (!isEditMode) return;

    setInitialLoading(true);
    try {
      const blogData = await getBlogById(id);
      setFormData({
        title: blogData.title || "",
        abstract: blogData.abstract || "",
        description: blogData.description || "",
        image: null,
        imagePreview: blogData.imageUrl || null,
      });
    } catch (error) {
      console.error("Error fetching blog:", error);
      setSnackbarMessage("خطا در دریافت اطلاعات مقاله");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setInitialLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    document.title = pageTitle;
    if (isEditMode) {
      fetchData();
    }
  }, [fetchData, isEditMode, pageTitle]);

  // ============================================
  // Render
  // ============================================

  if (initialLoading) {
    return <Loader />;
  }

  return (
    <Content>
      <div className="container color-blue mb-5 blog-editable">
        {/* Header */}
        <div className="row mt-4 mb-4 align-items-center">
          <div className="col-auto">
            <IconButton onClick={() => navigate(-1)} sx={{ color: "#053251" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div className="col">
            <h2 className="mb-0 fw-bold color-blue iran-sans">{pageTitle}</h2>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            {/* Title Input */}
            <div className="row mb-4">
              <div className="col-12">
                <FormInput
                  className="form-blue"
                  name="عنوان مقاله"
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  value={formData.title}
                  for="title"
                  placeholder="عنوان مقاله را وارد کنید"
                  required={true}
                />
              </div>
            </div>

            {/* Abstract Input */}
            <div className="row mb-4">
              <div className="col-12">
                <FormInput
                  className="form-blue"
                  name="چکیده مقاله"
                  for="abstract"
                  type="textarea"
                  value={formData.abstract}
                  onChange={(e) =>
                    handleInputChange("abstract", e.target.value)
                  }
                  placeholder="یک خلاصه کوتاه از محتوای مقاله"
                  height="100px"
                  required={true}
                />
              </div>
            </div>

            {/* Featured Image Upload */}
            <div className="row mb-4">
              <div className="col-12">
                <div
                  className="p-4"
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    border: "2px dashed #dee2e6",
                  }}
                >
                  <div className="d-flex align-items-center mb-3">
                    <ImageIcon
                      sx={{ fontSize: 28, color: "#7cc890", marginLeft: 2 }}
                    />
                    <div>
                      <h6 className="mb-0 fw-bold color-blue iran-sans">
                        تصویر شاخص مقاله
                      </h6>
                      <small className="text-muted iran-sans">
                        این تصویر در لیست مقالات و بالای صفحه نمایش داده می‌شود
                      </small>
                    </div>
                  </div>

                  {formData.imagePreview ? (
                    <div className="position-relative">
                      <img
                        src={formData.imagePreview}
                        alt="Preview"
                        style={{
                          width: "100%",
                          maxHeight: "300px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          background: "rgba(220, 53, 69, 0.9)",
                          color: "white",
                          "&:hover": {
                            background: "rgba(220, 53, 69, 1)",
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImageChange}
                      />
                      <Button
                        variant="outlined"
                        className="iran-sans"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          borderColor: "#7cc890",
                          color: "#7cc890",
                          "&:hover": {
                            borderColor: "#6ab87d",
                            background: "rgba(124, 200, 144, 0.1)",
                          },
                        }}
                      >
                        انتخاب تصویر
                      </Button>
                      <small className="text-muted iran-sans d-block mt-2">
                        فرمت‌های مجاز: JPG, PNG, GIF | حداکثر حجم: 5MB
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rich Text Editor */}
            <div className="row mb-4">
              <div className="col-12">
                <label className="form-label mb-3 fw-bold color-blue iran-sans">
                  محتوای مقاله
                  <span className="text-danger"> *</span>
                </label>

                <div
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Suspense fallback={<Loader />}>
                  <ArticleEditor
                    ref={editorRef}
                    value={formData.description}
                    onChange={(html) => handleInputChange("description", html)}
                    disabled={loading}
                  />
                  </Suspense>
                </div>

                <small className="text-muted iran-sans d-block mt-2">
                  💡 نکته: می‌توانید تصویر را با Drag & Drop یا Copy/Paste به
                  داخل محتوا اضافه کنید
                </small>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex gap-3 flex-wrap">
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    className="iran-sans"
                    sx={{
                      background: "linear-gradient(135deg, #7cc890, #6ab87d)",
                      color: "white",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      boxShadow: "0 4px 15px rgba(124, 200, 144, 0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #6ab87d, #5aa76a)",
                        boxShadow: "0 6px 20px rgba(124, 200, 144, 0.4)",
                      },
                      "&:disabled": {
                        background: "#ccc",
                      },
                    }}
                  >
                    {loading
                      ? "در حال ذخیره..."
                      : isEditMode
                      ? "ذخیره تغییرات"
                      : "انتشار مقاله"}
                  </Button>

                  <Button
                    onClick={handleReset}
                    disabled={loading}
                    variant="outlined"
                    className="iran-sans"
                    sx={{
                      borderColor: "#dc3545",
                      color: "#dc3545",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      "&:hover": {
                        borderColor: "#c82333",
                        background: "rgba(220, 53, 69, 0.1)",
                      },
                    }}
                  >
                    پاک کردن فرم
                  </Button>

                  <Button
                    onClick={() => navigate(-1)}
                    disabled={loading}
                    variant="text"
                    className="iran-sans"
                    sx={{
                      color: "#6c757d",
                      padding: "10px 30px",
                      fontSize: "16px",
                      "&:hover": {
                        background: "rgba(108, 117, 125, 0.1)",
                      },
                    }}
                  >
                    انصراف
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="row mt-4">
          <div className="col-md-6 mb-3">
            <div
              className="p-3 rounded-3"
              style={{
                background:
                  "linear-gradient(135deg, rgba(124, 200, 144, 0.1), rgba(124, 200, 144, 0.05))",
                border: "1px solid rgba(124, 200, 144, 0.3)",
              }}
            >
              <h6 className="fw-bold color-blue iran-sans mb-2">
                💡 راهنمای سریع
              </h6>
              <ul className="iran-sans mb-0" style={{ fontSize: "14px" }}>
                <li>برای افزودن تصویر در متن، از دکمه تصویر استفاده کنید</li>
                <li>می‌توانید تصاویر را Copy/Paste کنید</li>
                <li>از Heading ها برای سازماندهی محتوا استفاده کنید</li>
              </ul>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div
              className="p-3 rounded-3"
              style={{
                background:
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))",
                border: "1px solid rgba(59, 130, 246, 0.3)",
              }}
            >
              <h6 className="fw-bold color-blue iran-sans mb-2">ℹ️ نکات مهم</h6>
              <ul className="iran-sans mb-0" style={{ fontSize: "14px" }}>
                <li>عنوان و چکیده در نتایج جستجو نمایش داده می‌شوند</li>
                <li>تصویر شاخص در کارت مقاله نمایش داده می‌شود</li>
                <li>محتوا را با دقت بنویسید و قبل از انتشار بررسی کنید</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Loader Overlay */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
        </div>
      )}

      {/* Notifications */}
      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </Content>
  );
};

export default BlogEditable;
